// UI-only state: theme, active workspace filter, favorites, recents.
// Personal history is persisted through the API; localStorage is a per-account cache. Domain data never lives here — it goes through /api.

export interface EntityRef { entity_type: string; entity_id: string }
export interface Recent extends EntityRef { at: string }
export interface UiState { theme: string; workspaceId: string | null; likes: string[]; following: string[]; favorites: EntityRef[] | null; recents: Recent[] | null }

let key = 'cf-proto-ui-v1:anonymous';
let userId: string | null = null;
let writes: Promise<void> = Promise.resolve();
let mutations = 0;
const failedHomeWrites = new Set<string>();

const defaults: UiState = {
  theme: 'studio',
  workspaceId: null, // null = All Workspaces
  likes: [],         // gallery item ids liked in this browser
  following: [],     // creator ids
  favorites: null,   // loaded from the authenticated profile
  recents: null,
};

function normalizeTheme(theme: unknown): 'studio' | 'dark' {
  return theme === 'dark' ? 'dark' : 'studio';
}

function load(): UiState {
  try {
    // Keep the previous visual theme without importing shared personal history.
    const legacyTheme = JSON.parse(localStorage.getItem('cf-proto-ui-v1') || '{}').theme;
    const stored = JSON.parse(localStorage.getItem(key) || '{}');
    return { ...defaults, ...stored, theme: normalizeTheme(stored.theme ?? legacyTheme) };
  }
  catch { return { ...defaults }; }
}

let state = load();
const subs = new Set<(state: UiState) => void>();

function save() {
  try { localStorage.setItem(key, JSON.stringify(state)); } catch { /* storage blocked — keep in memory */ }
}

export function get() { return state; }

export function set(patch: Partial<UiState>) {
  state = { ...state, ...patch, ...(patch.theme === undefined ? {} : { theme: normalizeTheme(patch.theme) }) };
  save();
  subs.forEach((fn) => fn(state));
}

export function subscribe(fn: (state: UiState) => void) { subs.add(fn); return () => subs.delete(fn); }

export async function initializeUser(id: string, home?: { favorites: EntityRef[]; recents: Recent[] }) {
  userId = id;
  failedHomeWrites.clear();
  mutations++;
  key = `cf-proto-ui-v1:${id}`;
  state = { ...load(), favorites: [], recents: [] };
  if (home) set({ favorites: home.favorites, recents: home.recents });
  else await refreshHome();
}

export async function refreshHome() {
  await writes;
  const owner = userId, version = mutations;
  if (!owner) return;
  const response = await fetch('/api/v1/home-preferences');
  if (!response.ok) throw new Error(`Cannot load personal Home (${response.status})`);
  const home = await response.json();
  if (userId === owner && mutations === version) set({ favorites: home.favorites || [], recents: home.recents || [] });
}

function persist(operation: { action: 'touch' | 'favorite'; entity_type: string; entity_id: string; enabled?: boolean }) {
  if (!userId) return Promise.resolve(false);
  let saved = false;
  const owner = userId;
  const operationKey = `${owner}:${operation.action}:${operation.entity_type}:${operation.entity_id}`;
  const version = ++mutations;
  writes = writes.then(async () => {
    if (userId !== owner) return;
    const response = await fetch('/api/v1/home-preferences', {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(operation),
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      const detail = body.error === 'Object unavailable'
        ? `The ${operation.entity_type} is unavailable or access was denied.`
        : body.error || `HTTP ${response.status}`;
      throw new Error(`Could not save Home ${operation.action === 'touch' ? 'recent history' : 'favorites'}. ${detail}`);
    }
    const home = await response.json();
    if (userId === owner && mutations === version) set({ favorites: home.favorites || [], recents: home.recents || [] });
    failedHomeWrites.delete(operationKey);
    if (!failedHomeWrites.size) window.dispatchEvent(new CustomEvent('cf:home-saved'));
    saved = true;
  }).catch(error => {
    if (userId === owner) {
      failedHomeWrites.add(operationKey);
      window.dispatchEvent(new CustomEvent('cf:persistence-error', { detail: error.message }));
    }
  });
  return writes.then(() => saved);
}

export const whenHomeSaved = () => writes;

// favorites: [{ entity_type, entity_id }]
export function isFavorite(type: string, id: string) {
  return (state.favorites || []).some((f) => f.entity_type === type && f.entity_id === id);
}

export function toggleFavorite(type: string, id: string) {
  const list = state.favorites || [];
  const enabled = !isFavorite(type, id);
  set({
    favorites: !enabled
      ? list.filter((f) => !(f.entity_type === type && f.entity_id === id))
      : [{ entity_type: type, entity_id: id }, ...list],
  });
  return persist({ action: 'favorite', entity_type: type, entity_id: id, enabled });
}

// recents: [{ entity_type, entity_id, at }], newest first, capped
export function touchRecent(type: string, id: string) {
  const list = (state.recents || []).filter((r) => !(r.entity_type === type && r.entity_id === id));
  set({ recents: [{ entity_type: type, entity_id: id, at: new Date().toISOString() }, ...list].slice(0, 50) });
  persist({ action: 'touch', entity_type: type, entity_id: id });
}

export const isLiked = (id: string) => (state.likes || []).includes(id);
export function toggleLike(id: string) {
  const on = !isLiked(id);
  set({ likes: on ? [id, ...(state.likes || [])] : state.likes.filter((x) => x !== id) });
  return on;
}
export const isFollowing = (id: string) => (state.following || []).includes(id);
export function toggleFollow(id: string) {
  const on = !isFollowing(id);
  set({ following: on ? [id, ...(state.following || [])] : state.following.filter((x) => x !== id) });
  return on;
}

export function resetUi() {
  try { localStorage.removeItem(key); } catch { /* ignore */ }
  state = { ...defaults };
  subs.forEach((fn) => fn(state));
}
